import React, { useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar/context';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';
import TabsIcon from '@atlaskit/icon-lab/core/tabs';

import { nativeTabsToolbarMessages as messages } from '../messages.ts';
import type { NativeTabsToolbarPlugin } from '../nativeTabsToolbarPluginType.ts';

type TabsButtonProps = {
	api?: ExtractInjectionAPI<NativeTabsToolbarPlugin>;
};

const NATIVE_TABS_QUICK_INSERT_KEY = 'native-tabs:native-tabs';
const TABS_BUTTON_TEST_ID = 'editor-toolbar-tabs-button';

export const TabsButton = ({ api }: TabsButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { editorView } = useEditorToolbar();
	const cachedTabsItem = useRef<QuickInsertItem | null>(null);

	if (!api?.quickInsert) {
		return null;
	}

	const onClick = () => {
		if (editorView) {
			let tabsItem = cachedTabsItem.current;

			if (!tabsItem) {
				const suggestions = api?.quickInsert?.actions?.getSuggestions({
					query: '',
					disableDefaultItems: false,
				});

				tabsItem = suggestions?.find((item) => item.key === NATIVE_TABS_QUICK_INSERT_KEY) ?? null;
				if (tabsItem) {
					cachedTabsItem.current = tabsItem;
				}
			}

			if (tabsItem) {
				const { state, dispatch } = editorView;
				api?.quickInsert?.actions?.insertItem(tabsItem, INPUT_METHOD.TOOLBAR)(state, dispatch);
			}
		}
	};

	return (
		<ToolbarTooltip content={formatMessage(messages.tabs)}>
			<ToolbarButton
				iconBefore={<TabsIcon label={formatMessage(messages.tabs)} size="small" />}
				onClick={onClick}
				testId={TABS_BUTTON_TEST_ID}
			/>
		</ToolbarTooltip>
	);
};
